import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown, Plus, CalendarIcon, Search } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { EquipmentCache, type TipoEquipamento, type Equipamento } from '@/services/equipmentCache';

interface DialogAddEquipmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (equipmentData: any) => void;
}

interface AddEquipmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
}

const AddEquipmentModal: React.FC<AddEquipmentModalProps> = ({ open, onOpenChange, onSave }) => {
  const [activeTab, setActiveTab] = useState<'equipamento' | 'tipo'>('equipamento');
  const [equipamentoData, setEquipamentoData] = useState({
    tipo: '',
    identificacao: '',
    condicao: 'Bom',
    fabricante: '',
    modelo: '',
    ativo: true
  });
  const [tipoData, setTipoData] = useState({
    nome: '',
    categoria: '',
    descricao: ''
  });
  const { toast } = useToast();

  const tipos = useMemo(() => EquipmentCache.getTiposEquipamentos(), []);

  const handleSaveEquipamento = () => {
    if (!equipamentoData.tipo) {
      toast({
        title: "Erro",
        description: "Tipo é obrigatório.",
        variant: "destructive"
      });
      return;
    }

    EquipmentCache.addEquipamento({
      ...equipamentoData,
      id: '',
      createdAt: '',
      updatedAt: ''
    });

    setEquipamentoData({
      tipo: '',
      identificacao: '',
      condicao: 'Bom',
      fabricante: '',
      modelo: '',
      ativo: true
    });

    toast({
      title: "Sucesso",
      description: "Equipamento adicionado com sucesso!"
    });

    onSave();
    onOpenChange(false);
  };

  const handleSaveTipo = () => {
    if (!tipoData.nome) {
      toast({
        title: "Erro",
        description: "Nome do tipo é obrigatório.",
        variant: "destructive"
      });
      return;
    }

    EquipmentCache.addTipoEquipamento({
      ...tipoData,
      id: '',
      createdAt: '',
      updatedAt: ''
    });

    setTipoData({
      nome: '',
      categoria: '',
      descricao: ''
    });

    toast({
      title: "Sucesso",
      description: "Tipo de equipamento adicionado com sucesso!"
    });

    onSave();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Novo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex border rounded-lg">
            <button
              className={cn(
                "flex-1 py-2 px-4 text-sm rounded-l-lg transition-colors",
                activeTab === 'equipamento' 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
              onClick={() => setActiveTab('equipamento')}
            >
              Equipamento
            </button>
            <button
              className={cn(
                "flex-1 py-2 px-4 text-sm rounded-r-lg transition-colors",
                activeTab === 'tipo' 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
              onClick={() => setActiveTab('tipo')}
            >
              Tipo
            </button>
          </div>

          {activeTab === 'equipamento' ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="tipo">Tipo *</Label>
                <Input
                  id="tipo"
                  value={equipamentoData.tipo}
                  onChange={(e) => setEquipamentoData(prev => ({ ...prev, tipo: e.target.value }))}
                  placeholder="Ex: Betoneira"
                  list="tipos-list"
                />
                <datalist id="tipos-list">
                  {tipos.map(t => (
                    <option key={t.id} value={t.nome} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label htmlFor="identificacao">Identificação</Label>
                <Input
                  id="identificacao"
                  value={equipamentoData.identificacao}
                  onChange={(e) => setEquipamentoData(prev => ({ ...prev, identificacao: e.target.value }))}
                  placeholder="Ex: BT-001"
                />
              </div>
              <div>
                <Label htmlFor="fabricante">Fabricante</Label>
                <Input
                  id="fabricante"
                  value={equipamentoData.fabricante}
                  onChange={(e) => setEquipamentoData(prev => ({ ...prev, fabricante: e.target.value }))}
                  placeholder="Ex: Caterpillar"
                />
              </div>
              <div>
                <Label htmlFor="modelo">Modelo</Label>
                <Input
                  id="modelo"
                  value={equipamentoData.modelo}
                  onChange={(e) => setEquipamentoData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="Ex: 320D2 GC"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="tipo-nome">Nome do Tipo *</Label>
                <Input
                  id="tipo-nome"
                  value={tipoData.nome}
                  onChange={(e) => setTipoData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Betoneira"
                />
              </div>
              <div>
                <Label htmlFor="categoria">Categoria</Label>
                <Input
                  id="categoria"
                  value={tipoData.categoria}
                  onChange={(e) => setTipoData(prev => ({ ...prev, categoria: e.target.value }))}
                  placeholder="Ex: Misturador"
                />
              </div>
              <div>
                <Label htmlFor="tipo-descricao">Descrição</Label>
                <Input
                  id="tipo-descricao"
                  value={tipoData.descricao}
                  onChange={(e) => setTipoData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição do tipo de equipamento"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={activeTab === 'equipamento' ? handleSaveEquipamento : handleSaveTipo}>
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DialogAddEquipment: React.FC<DialogAddEquipmentProps> = ({
  open,
  onOpenChange,
  onAdd
}) => {
  const [activeTab, setActiveTab] = useState("existing");
  const [selectedItem, setSelectedItem] = useState("");
  const [comboOpen, setComboOpen] = useState(false);
  const [addEquipmentModalOpen, setAddEquipmentModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { toast } = useToast();

  // Form states
  const [equipmentData, setEquipmentData] = useState({
    operator: "",
    ownershipType: "",
    leaseExpiryDate: undefined as Date | undefined,
    quantity: 1
  });

  // Buscar dados do cache
  const tipos = useMemo(() => EquipmentCache.getTiposEquipamentos(), [refreshKey]);
  const equipamentos = useMemo(() => EquipmentCache.getEquipamentos().filter(e => e.ativo), [refreshKey]);

  // Combinar tipos e equipamentos para a lista
  const allItems = useMemo(() => {
    const items = [
      {
        id: 'add-new',
        nome: 'Adicionar Novo Equipamento',
        type: 'action',
        identificacao: ''
      }
    ];

    // Adicionar tipos primeiro
    tipos.forEach(t => {
      items.push({
        id: `tipo-${t.id}`,
        nome: t.nome,
        type: 'tipo',
        identificacao: t.categoria || 'Tipo'
      });
    });

    // Depois equipamentos
    equipamentos.forEach(e => {
      items.push({
        id: `equipamento-${e.id}`,
        nome: e.tipo,
        type: 'equipamento',
        identificacao: e.identificacao || ''
      });
    });

    return items;
  }, [tipos, equipamentos]);

  // Filtrar itens por busca
  const filteredItems = useMemo(() => {
    if (!searchTerm) return allItems;
    
    return allItems.filter(item => 
      item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.identificacao.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allItems, searchTerm]);

  const handleAddExisting = () => {
    if (!selectedItem || selectedItem === 'add-new') {
      toast({
        title: "Erro",
        description: "Selecione um equipamento ou tipo.",
        variant: "destructive"
      });
      return;
    }

    const item = allItems.find(i => i.id === selectedItem);
    if (!item) return;

    onAdd({
      type: item.nome,
      identification: item.identificacao,
      condition: "Bom",
      quantity: equipmentData.quantity,
      ...equipmentData
    });

    // Reset form
    setSelectedItem("");
    setEquipmentData({
      operator: "",
      ownershipType: "",
      leaseExpiryDate: undefined,
      quantity: 1
    });
    onOpenChange(false);

    toast({
      title: "Sucesso",
      description: "Equipamento adicionado com sucesso!"
    });
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar Equipamento</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>

            <TabsContent value="existing" className="space-y-4">
              <div>
                <Label>Selecionar Tipo ou Equipamento</Label>
                <Popover open={comboOpen} onOpenChange={setComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboOpen}
                      className="w-full justify-between"
                    >
                      {selectedItem === 'add-new' 
                        ? "Adicionar Novo Equipamento"
                        : selectedItem 
                        ? allItems.find(i => i.id === selectedItem)?.nome
                        : "Buscar tipos e equipamentos..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                          placeholder="Buscar por nome ou identificação..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </div>
                      <CommandList>
                        <CommandEmpty>
                          Nenhum resultado encontrado.
                        </CommandEmpty>
                        <CommandGroup>
                          {filteredItems.map((item) => (
                            <CommandItem
                              key={item.id}
                              value={item.nome}
                              onSelect={() => {
                                if (item.id === 'add-new') {
                                  setAddEquipmentModalOpen(true);
                                  setComboOpen(false);
                                } else {
                                  setSelectedItem(item.id);
                                  setComboOpen(false);
                                }
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedItem === item.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  {item.id === 'add-new' ? (
                                    <Plus className="w-4 h-4 text-primary" />
                                  ) : null}
                                  <div className="font-medium">{item.nome}</div>
                                </div>
                                {item.identificacao && item.id !== 'add-new' && (
                                  <div className="text-sm text-muted-foreground">
                                    {item.type === 'tipo' ? 'Tipo' : item.identificacao}
                                  </div>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </TabsContent>
          </Tabs>

          {/* Common fields */}
          <div className="space-y-4 border-t pt-4">
            <div>
              <Label htmlFor="operator">Operador</Label>
              <Input
                id="operator"
                value={equipmentData.operator}
                onChange={(e) => setEquipmentData(prev => ({ ...prev, operator: e.target.value }))}
                placeholder="Nome do operador"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Quantidade</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={equipmentData.quantity}
                  onChange={(e) => setEquipmentData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  placeholder="1"
                />
              </div>
              <div>
                <Label>Tipo de Equipamento</Label>
                <Select
                  value={equipmentData.ownershipType}
                  onValueChange={(value) => setEquipmentData(prev => ({ ...prev, ownershipType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proprio">Próprio</SelectItem>
                    <SelectItem value="locado">Locado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {equipmentData.ownershipType === "locado" && (
                <div>
                  <Label>Data de Vencimento</Label>
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !equipmentData.leaseExpiryDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {equipmentData.leaseExpiryDate ? format(equipmentData.leaseExpiryDate, "PPP", { locale: ptBR }) : "Selecionar data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={equipmentData.leaseExpiryDate}
                         onSelect={(date) => {
                           setEquipmentData(prev => ({ ...prev, leaseExpiryDate: date }));
                           setIsCalendarOpen(false);
                         }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddExisting}>
              Adicionar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AddEquipmentModal 
        open={addEquipmentModalOpen} 
        onOpenChange={setAddEquipmentModalOpen}
        onSave={refreshData}
      />
    </>
  );
};